import { FC, useEffect, useState } from "react";
import { cn } from "@/libs/utils";
import { Portal } from "src/components/portal/Portal";
import { useTrans } from "src/utils/useTrans";
import SelectCategoryOnMobile from "./select_category_on_mobile";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import SearchIcon from "@/v2-components/core/icon/search-icon";

type SortOption = { value: string; labelKey: string };

type Props = {
  className?: string;
  categories: string[];
  onUpdate: (update: { category: string; search: string }) => void;
  sortBy: string;
  sortOptions: SortOption[];
  onSortChange: (value: string) => void;
};

const FilterAndSearchNewsFeed: FC<Props> = ({
  onUpdate,
  className,
  categories,
  sortBy,
  sortOptions,
  onSortChange,
}) => {
  const { t } = useTrans(["support"]);

  const [current, setCurrent] = useState({
    category: categories[0] ?? "all",
    search: "",
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const body = document.getElementsByTagName("body")[0];
    if (body) {
      if (open) {
        body.style.overflowY = "hidden";
      } else {
        body.style.overflowY = "";
      }
    }
  }, [open]);

  useEffect(() => {
    onUpdate(current);
  }, [current, onUpdate]);

  const updateCategory = (category: string) => (e: any) => {
    setCurrent((state: any) => ({ ...state, category }));
  };

  const updateSearch = (e: any) => {
    setCurrent((state: any) => ({ ...state, search: e.target.value }));
  };

  const toggleOpen = (e: any) => {
    e.stopPropagation();
    setOpen(!open);
  };

  return (
    <div className={cn(className, "")}>
      {open && (
        <Portal className={"fixed inset-0 z-50 bg-white"}>
          <SelectCategoryOnMobile
            categories={categories}
            onClose={toggleOpen}
            onSelectCategory={updateCategory}
            current={current.category}
            sortBy={sortBy}
            sortOptions={sortOptions}
            onSelectSort={onSortChange}
          />
        </Portal>
      )}
      <div className={"mb-5 flex flex-row sm:mb-0 sm:flex-col"}>
        <span
          className={
            "whitespace-no-wrap text-[1.375rem] font-700 leading-tight sm:mb-2.5 sm:whitespace-pre-wrap lg:text-3xl"
          }
        >
          {t("News Feed")}
        </span>
        {/* 정렬 select — 데스크톱 전용. 모바일은 카테고리 dialog 안에서 선택. */}
        <div className={"hidden sm:mt-4 sm:block"}>
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            aria-label={t("Sort by")}
            className={
              "w-full max-w-[12rem] cursor-pointer rounded border border-[#222222] px-3 py-2 text-sm font-700 outline-none"
            }
          >
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div
          className={"flex w-full flex-col items-end sm:items-start sm:py-12"}
        >
          {categories.map((category, index) => {
            const on =
              current.category.toLowerCase() === category.toLowerCase();
            return (
              <div
                key={index}
                className={cn(
                  "pointer-events-auto cursor-pointer items-center sm:pointer-events-none",
                  "flex",
                  on ? "" : "hidden sm:flex",
                )}
                onClick={toggleOpen}
              >
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    updateCategory(category)(e);
                  }}
                  className={cn(
                    on
                      ? "opacity-100"
                      : "cursor-pointer opacity-50 hover:opacity-100",
                    "pointer-events-none my-2 text-sm font-700 capitalize sm:pointer-events-auto",
                  )}
                >
                  {t(`${category}`)}
                </span>
                <ChevronDownIcon className="size-5 sm:hidden" />
              </div>
            );
          })}
        </div>
      </div>
      <div className={"flex w-full flex-col sm:w-3/4 sm:max-w-screen-xs"}>
        <div className={"flex items-center justify-between py-4"}>
          <input
            onChange={updateSearch}
            value={current.search}
            type={"text"}
            placeholder={t("Search") || ""}
            className={"ellipsis text-sm outline-none"}
          />
          <SearchIcon className="size-4.5" />
        </div>
        <span className={"h-px bg-[#222222]"} />
      </div>
    </div>
  );
};

export default FilterAndSearchNewsFeed;
